import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Typography, Tabs, Tab, Alert, Paper } from '@mui/material';
import { UserLabelSelector } from './UserLabelSelector';
import { StorageLabelSelector } from './StorageLabelSelector';
import { LabelPrintPreview } from './LabelPrintPreview';
import { LabelPrintJob, LabelType } from '../types';
import { useGlobalStore } from '../../../application/store/store';
import { UserRoleType } from '../../../application/types/userRoleType';

/**
 * LabelPrintView - Main container for label printing feature.
 *
 * Features:
 * - RBAC enforcement (LocalAdmin, Admin, or Root required)
 * - Tab selection for user/storage labels (US2 will add storage tab)
 * - Integration of UserLabelSelector and LabelPrintPreview
 * - State management for current print job
 */
export const LabelPrintView: React.FC = () => {
    const { t } = useTranslation();
    const { profile } = useGlobalStore();
    const [labelType, setLabelType] = useState<LabelType>('user');
    const [currentJob, setCurrentJob] = useState<LabelPrintJob | null>(null);
    const [hasAccess, setHasAccess] = useState<boolean>(false);

    // Check RBAC - user must have extended role permissions
    useEffect(() => {
        if (!profile?.userRoles) {
            setHasAccess(false);
            return;
        }

        const hasExtendedRole = profile.userRoles.some(
            (role) =>
                role.roleName.toLowerCase().trim() ===
                    UserRoleType.ControlledSubstancesOfficer.toLowerCase().trim() ||
                role.roleName.toLowerCase().trim() ===
                    UserRoleType.MedicationsSafetyOfficer.toLowerCase().trim() ||
                role.roleName.toLowerCase().trim() ===
                    UserRoleType.OperationalManager.toLowerCase().trim() ||
                role.roleName.toLowerCase().trim() ===
                    UserRoleType.LocalAdmin.toLowerCase().trim() ||
                role.roleName.toLowerCase().trim() ===
                    UserRoleType.Admin.toLowerCase().trim() ||
                role.roleName.toLowerCase().trim() ===
                    UserRoleType.Root.toLowerCase().trim()
        );

        setHasAccess(hasExtendedRole);
    }, [profile]);

    // Handle tab change
    const handleTabChange = (
        _event: React.SyntheticEvent,
        newValue: LabelType
    ) => {
        setLabelType(newValue);
        setCurrentJob(null); // Clear current job when switching tabs
    };

    // Handle job creation
    const handleJobCreated = (job: LabelPrintJob) => {
        setCurrentJob(job);
    };

    // Handle back from preview
    const handleBackToSelection = () => {
        setCurrentJob(null);
    };

    // Access denied - redirect or show message
    if (!hasAccess) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4 }}>
                <Alert severity="error">{t('errors.accessDenied')}</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" gutterBottom>
                {t('labelPrinting.title')}
            </Typography>

            {!currentJob ? (
                <>
                    {/* Tab selector for user/storage labels */}
                    <Paper elevation={1} sx={{ mb: 3 }}>
                        <Tabs
                            value={labelType}
                            onChange={handleTabChange}
                            aria-label={t('labelPrinting.labelTypeSelection')}
                        >
                            <Tab label={t('common.user')} value="user" />
                            <Tab
                                label={t('common.storageLocations')}
                                value="storage"
                            />
                        </Tabs>
                    </Paper>

                    {/* Selection component based on type */}
                    <Paper elevation={2} sx={{ p: 3 }}>
                        {labelType === 'user' && (
                            <UserLabelSelector
                                onJobCreated={handleJobCreated}
                            />
                        )}
                        {labelType === 'storage' && (
                            <StorageLabelSelector
                                onJobCreated={handleJobCreated}
                            />
                        )}
                    </Paper>
                </>
            ) : (
                <>
                    {/* Print preview */}
                    <LabelPrintPreview
                        job={currentJob}
                        onBack={handleBackToSelection}
                    />
                </>
            )}
        </Container>
    );
};
